// src/components/student/StudentLayout.jsx
// The logged-in student's app shell: left sidebar on desktop, slide-in drawer on mobile.
// Replaces the marketing Header/Footer for the student experience.
import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  HiHome, HiClipboardList, HiAcademicCap, HiTrendingUp,
  HiChartBar, HiUserCircle, HiLogout, HiMenu, HiX, HiBookmark,
} from "react-icons/hi";
import logo from "../assets/images/logos-ravi.png";
import { useAuth } from "../../context/AuthContext";
import { levelProgress } from "../../lib/gamification";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: HiHome, emoji: "🏠" },
  { to: "/mcq", label: "MCQ Tests", icon: HiClipboardList, emoji: "📝" },
  { to: "/practice", label: "Study Materials", icon: HiAcademicCap, emoji: "📚" },
  { to: "/library", label: "My Library", icon: HiBookmark, emoji: "📑" },
  { to: "/progress", label: "Progress", icon: HiTrendingUp, emoji: "📈" },
  { to: "/leaderboard", label: "Leaderboard", icon: HiChartBar, emoji: "🏆" },
  { to: "/profile", label: "Profile", icon: HiUserCircle, emoji: "👤" },
];

function SidebarContent({ profile, onNavigate, onLogout }) {
  const lp = levelProgress(profile?.xp || 0);
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-5 py-5 border-b border-gray-100">
        <NavLink to="/dashboard" onClick={onNavigate} className="flex items-center">
          <img src={logo} alt="Ravi TestPapers" className="h-12" />
        </NavLink>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            // /mcq should not stay active when on /mcq/:id (that's full-screen anyway)
            end={item.to === "/mcq"}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all ${
                isActive
                  ? "bg-indigo-100 text-indigo-700 shadow-sm"
                  : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
              }`
            }
          >
            <span className="text-lg">{item.emoji}</span>
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Level / XP mini-widget */}
      <div className="px-3 pb-3">
        <div className="rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 p-4 text-white">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold uppercase tracking-wide text-indigo-100">Level {profile?.level || 1}</span>
            <span className="text-xs font-bold text-indigo-100">{profile?.xp || 0} XP</span>
          </div>
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all" style={{ width: `${lp.pct}%` }} />
          </div>
          <p className="text-[10px] text-indigo-100 mt-1.5">{lp.toNext} XP to Level {lp.nextLevel}</p>
        </div>

        <button
          onClick={onLogout}
          className="w-full mt-2 flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold text-red-500 hover:bg-red-50 transition-colors"
        >
          <HiLogout className="text-lg" /> Logout
        </button>
      </div>
    </div>
  );
}

export default function StudentLayout({ children }) {
  const { profile, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogout = async () => {
    setDrawerOpen(false);
    await logout();
    navigate("/");
  };

  // Friendly page title for the mobile top bar
  const current = NAV.find((n) => location.pathname.startsWith(n.to));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Desktop sidebar ── */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-60 bg-white border-r border-gray-100 z-40">
        <SidebarContent profile={profile} onNavigate={() => {}} onLogout={handleLogout} />
      </aside>

      {/* ── Mobile top bar ── */}
      <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <img src={logo} alt="Ravi TestPapers" className="h-10" />
        <span className="font-extrabold text-gray-700 text-sm">{current?.label || ""}</span>
        <button onClick={() => setDrawerOpen(true)} className="text-gray-600 p-1" aria-label="Open menu">
          <HiMenu className="text-2xl" />
        </button>
      </div>

      {/* ── Mobile drawer ── */}
      <AnimatePresence>
        {drawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDrawerOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/40 z-40"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.25 }}
              className="lg:hidden fixed inset-y-0 left-0 w-72 bg-white z-50 shadow-2xl"
            >
              <button
                onClick={() => setDrawerOpen(false)}
                className="absolute top-4 right-4 text-gray-400 p-1 z-10"
                aria-label="Close menu"
              >
                <HiX className="text-2xl" />
              </button>
              <SidebarContent
                profile={profile}
                onNavigate={() => setDrawerOpen(false)}
                onLogout={handleLogout}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Main content ── */}
      <main className="lg:ml-60 min-h-screen">{children}</main>
    </div>
  );
}
